import { useState, useMemo } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import type {
  CyFunCategory,
  CyFunRequirement,
  CyFunSubcategory,
  MaturityEvaluationRubric,
} from "@/types/maturity";
import { FullEvalSubcategoryCard } from "../FullEvalSubcategoryCard";

type FullEvalCategorySectionProps = {
  category: CyFunCategory;
  subcategories: CyFunSubcategory[];
  requirements: CyFunRequirement[];
  keyMeasureCodes: string[];
  responses: Record<string, number>;
  notes: Record<string, string>;
  rubricsByCode: Map<string, MaturityEvaluationRubric[]>;
  domainColor: string;
  domainBg: string;
  onScoreChange: (key: string, value: number) => void;
  onNoteChange: (key: string, value: string) => void;
  onNoteBlur: () => void;
  defaultExpanded?: boolean;
};

function FullEvalCategorySection({
  category,
  subcategories,
  requirements,
  keyMeasureCodes,
  responses,
  notes,
  rubricsByCode,
  domainColor,
  domainBg,
  onScoreChange,
  onNoteChange,
  onNoteBlur,
  defaultExpanded = true,
}: FullEvalCategorySectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const categorySubcategories = useMemo(
    () => subcategories.filter((s) => s.category_code === category.code),
    [subcategories, category.code]
  );

  const categoryRequirements = useMemo(
    () =>
      requirements.filter((r) =>
        categorySubcategories.some((s) => s.code === r.subcategory_code)
      ),
    [requirements, categorySubcategories]
  );

  const answeredCount = useMemo(
    () =>
      categoryRequirements.filter(
        (req) =>
          responses[`${req.code}-D`] !== undefined &&
          responses[`${req.code}-I`] !== undefined
      ).length,
    [categoryRequirements, responses]
  );

  const totalCount = categoryRequirements.length;
  const isComplete = answeredCount === totalCount && totalCount > 0;

  const ChevronIcon = isExpanded ? ChevronDown : ChevronRight;

  return (
    <div className="rounded-xl overflow-hidden border border-slate-200">
      <div className="flex">
        <div
          className="w-1.5 shrink-0"
          style={{ backgroundColor: domainColor }}
        />
        <div className="flex-1">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
            style={{ backgroundColor: domainBg }}
          >
            <div className="flex items-center gap-2">
              <ChevronIcon className="w-4 h-4 text-slate-400 shrink-0" />
              <span className="text-xs font-mono font-semibold text-slate-400">
                {category.code}
              </span>
              <span className="text-sm font-semibold text-slate-700 text-left">
                {category.name}
              </span>
            </div>
            <span
              className={`text-xs font-medium font-mono px-2.5 py-0.5 rounded-full shrink-0 ${
                isComplete
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-white/80 text-slate-500"
              }`}
            >
              {answeredCount}/{totalCount}
            </span>
          </button>

          {isExpanded && (
            <div className="p-4 space-y-3">
              {categorySubcategories.map((subcat) => {
                const subcatReqs = categoryRequirements.filter(
                  (r) => r.subcategory_code === subcat.code
                );
                if (subcatReqs.length === 0) return null;

                return (
                  <FullEvalSubcategoryCard
                    key={subcat.code}
                    subcategory={subcat}
                    requirements={subcatReqs}
                    keyMeasureCodes={keyMeasureCodes}
                    responses={responses}
                    notes={notes}
                    rubricsByCode={rubricsByCode}
                    onScoreChange={onScoreChange}
                    onNoteChange={onNoteChange}
                    onNoteBlur={onNoteBlur}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default FullEvalCategorySection;
